import React, { useState, useEffect, useCallback } from 'react';
import { Icon } from '@iconify/react';
import { Link } from 'react-router-dom';
import NotificationModal from './NotificationModal';
import LogoutModal from './LogoutModal';
import { adminService } from '../../services/api';

const SUPER_NOTIF_READ_KEY = 'qease_super_notif_read_ids';

const SuperAdminLayout = ({ children, activePage, title }) => {
    const [isNotificationModalOpen, setIsNotificationModalOpen] = useState(false);
    const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
    const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);
    const [notificationFeed, setNotificationFeed] = useState([]);

    const loadReadIds = useCallback(() => {
        try {
            return new Set(JSON.parse(localStorage.getItem(SUPER_NOTIF_READ_KEY) || '[]'));
        } catch {
            return new Set();
        }
    }, []);

    const fetchNotifications = useCallback(async () => {
        try {
            const res = await adminService.getNotificationFeed();
            if (!res.data?.success) return;
            const read = loadReadIds();
            const rows = (res.data.data || []).map((n) => ({
                ...n,
                isRead: read.has(n.id),
            }));
            setNotificationFeed(rows);
        } catch (e) {
            console.error(e);
        }
    }, [loadReadIds]);

    useEffect(() => {
        fetchNotifications();
        const t = setInterval(fetchNotifications, 45000);
        return () => clearInterval(t);
    }, [fetchNotifications]);

    const markAllNotificationsRead = useCallback(() => {
        const ids = notificationFeed.map((n) => n.id);
        const merged = [...new Set([...loadReadIds(), ...ids])];
        localStorage.setItem(SUPER_NOTIF_READ_KEY, JSON.stringify(merged));
        setNotificationFeed((prev) => prev.map((n) => ({ ...n, isRead: true })));
    }, [notificationFeed, loadReadIds]);

    const toggleNotificationModal = () => {
        setIsNotificationModalOpen(!isNotificationModalOpen);
    };

    const toggleProfileDropdown = () => {
        setIsProfileDropdownOpen(!isProfileDropdownOpen);
    };

    return (
        <div className="flex min-h-screen bg-gray-50 font-jakarta">
            {/* Sidebar */}
            <aside className="w-64 bg-white border-r border-gray-100 flex flex-col fixed h-screen z-50">
                <div className="p-8">
                    <img src="/qease-logo.png" alt="Qease Logo" className="w-32" />
                </div>

                <nav className="flex-1 px-4 space-y-2">
                    <Link to="/super-admin" className={`flex items-center gap-4 px-4 py-3 rounded-xl transition-all ${activePage === 'dashboard' ? 'bg-[#06B6D4] text-white shadow-lg shadow-cyan-500/20' : 'text-gray-500 hover:bg-gray-50 hover:text-[#06B6D4]'}`}>
                        <Icon icon="material-symbols:dashboard-outline" className="text-xl" />
                        <span className="font-semibold text-sm">Dashboard</span>
                    </Link>
                    <Link to="/super-admin/clinics" className={`flex items-center gap-4 px-4 py-3 rounded-xl transition-all ${activePage === 'clinics' ? 'bg-[#06B6D4] text-white shadow-lg shadow-cyan-500/20' : 'text-gray-500 hover:bg-gray-50 hover:text-[#06B6D4]'}`}>
                        <Icon icon="material-symbols:grid-view-outline" className="text-xl" />
                        <span className="font-semibold text-sm">Klinik</span>
                    </Link>
                    <Link to="/super-admin/users" className={`flex items-center gap-4 px-4 py-3 rounded-xl transition-all ${activePage === 'users' ? 'bg-[#06B6D4] text-white shadow-lg shadow-cyan-500/20' : 'text-gray-500 hover:bg-gray-50 hover:text-[#06B6D4]'}`}>
                        <Icon icon="material-symbols:person-outline" className="text-xl" />
                        <span className="font-semibold text-sm">User</span>
                    </Link>
                </nav>
            </aside>

            {/* Main Content */}
            <main className="flex-1 ml-64 p-8">
                {/* Header */}
                <header className="flex justify-between items-center mb-8">
                    <div className="relative w-96">
                        <Icon icon="material-symbols:search" className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-xl" />
                        <input
                            type="text"
                            placeholder="Search"
                            className="w-full bg-white border border-gray-100 rounded-full py-3 pl-12 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-[#06B6D4]/20 transition-all shadow-sm"
                        />
                    </div>

                    <div className="flex items-center gap-6">
                        <div className="relative">
                            <div
                                onClick={toggleNotificationModal}
                                className="w-10 h-10 bg-white border border-gray-50 rounded-full flex items-center justify-center text-gray-500 shadow-sm cursor-pointer hover:bg-gray-50 transition-all"
                            >
                                <Icon icon="material-symbols:notifications-outline" className="text-xl" />
                            </div>
                            {notificationFeed.filter((n) => !n.isRead).length > 0 && (
                                <span className="absolute -top-1 -right-1 min-w-[1.25rem] h-5 px-1 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center border-2 border-white">
                                    {Math.min(99, notificationFeed.filter((n) => !n.isRead).length)}
                                </span>
                            )}
                        </div>

                        <div className="relative">
                            <div
                                onClick={toggleProfileDropdown}
                                className="flex items-center gap-3 pl-6 border-l border-gray-200 cursor-pointer hover:bg-gray-50/50 py-1 transition-all rounded-lg"
                            >
                                <div className="text-right">
                                    <p className="text-sm font-bold text-gray-800">Super Admin</p>
                                    <p className="text-xs text-gray-400">Super Admin</p>
                                </div>
                                <div className="w-10 h-10 rounded-full border-2 border-white shadow-sm flex items-center justify-center bg-gray-100">
                                    <Icon icon="material-symbols:account-circle" className="text-3xl text-gray-400" />
                                </div>
                                <Icon
                                    icon="material-symbols:keyboard-arrow-down"
                                    className={`text-gray-400 transition-transform ${isProfileDropdownOpen ? 'rotate-180' : ''}`}
                                />
                            </div>

                            {/* Profile Dropdown */}
                            {isProfileDropdownOpen && (
                                <>
                                    <div
                                        className="fixed inset-0 z-10"
                                        onClick={() => setIsProfileDropdownOpen(false)}
                                    />
                                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-xl border border-gray-100 py-2 z-20 animate-slideDown origin-top-right">
                                        <div className="mx-2 my-1 border-t border-gray-50" />
                                        <button
                                            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
                                            onClick={() => {
                                                setIsProfileDropdownOpen(false);
                                                setIsLogoutModalOpen(true);
                                            }}
                                        >
                                            <Icon icon="material-symbols:logout" className="text-lg" />
                                            <span className="font-medium">Logout</span>
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </header>

                <h1 className="text-2xl font-bold text-gray-800 mb-8">{title}</h1>

                {children}
            </main>

            {/* Notification Modal */}
            {isNotificationModalOpen && (
                <NotificationModal
                    isOpen={isNotificationModalOpen}
                    onClose={() => setIsNotificationModalOpen(false)}
                    notifications={notificationFeed}
                    onMarkAllRead={markAllNotificationsRead}
                />
            )}

            {/* Logout Confirmation Modal */}
            {isLogoutModalOpen && (
                <LogoutModal
                    isOpen={isLogoutModalOpen}
                    onClose={() => setIsLogoutModalOpen(false)}
                />
            )}
        </div>
    );
};

export default SuperAdminLayout;
